import { Card } from './Card'
import { launchDateLabel, type LaunchCountdown } from '../lib/launch'
import { useHubLang } from '../lib/i18n'
import { HOME } from '../lib/hubCopy'

/**
 * Days until the rebrand goes public. SHL-16.
 *
 * ── ONE DATE, AND IT IS THE THIRD OF THE THREE ──────────────────────────────
 *
 * There are three launch dates: managers 7 Oct 2026, employees 13 Oct 2026,
 * PUBLIC 15 Apr 2027. This card used to show the first two, which is the one
 * combination that makes no sense on a hub — by the time anybody can sign in
 * and read it, both have passed. The two internal dates belong to seed 008's
 * welcome notices, in front of the people being let in, on the day they are
 * let in. What is left worth counting is the only date still ahead of
 * everybody: the day the company's new name becomes public.
 *
 * ── THE NUMBER IS THE SERVER'S, NOT THE BROWSER'S ───────────────────────────
 *
 * This component used to take a hardcoded array from `lib/placeholder.ts` while
 * printing "counted against each site's own timezone, not the browser's"
 * underneath it. Both halves of that were wrong at once: the sentence
 * described a property the data did not have, and the data had been stale since
 * 30 August — it said 38 days to 7 October on a day when it was 17.
 *
 * `launch_countdown()` does the arithmetic in the database against
 * `app.resolve_person_timezone`, so the note is now true. Do not "simplify"
 * this by computing from `new Date()`: that is the viewer's clock, a crew in
 * Santiago and a crew in Burnaby would disagree, and the one who is wrong has
 * no way to tell. Rule 9.
 */
export function CountdownCard({
  countdown,
  locale,
}: {
  /** Null while loading, or when no date is set. The card renders nothing. */
  countdown: LaunchCountdown | null
  locale: string
}) {
  /* ABSENT, not zero. `launch_countdown()` REFUSES rather than returning an
     empty row when comms have set no date (rule 12), and a card reading
     "0 days until we go public" because nothing was configured is exactly the
     false claim that refusal exists to prevent. */
  if (!countdown) return null

  // Past the date, the number stops being a countdown and a negative one is
  // nonsense. Said in words instead — the day arrived, which is the only thing
  // left that is true.
  const arrived = countdown.daysRemaining <= 0
  // 113. The frame is hubCopy's; `countdown.label` already arrives in the
  // reader's language from launch_countdown() when Comms have worded it.
  const h = HOME[useHubLang()]

  return (
    <Card title={h.countdownTitle}>
      <div className="countdown countdown--single">
        <div className="countdown__cell">
          <p className="countdown__num">{arrived ? '—' : countdown.daysRemaining}</p>
          <p className="countdown__label">
            {arrived ? h.countdownArrived : h.countdownDays(countdown.label)}
            <br />
            <strong>{launchDateLabel(countdown.publicLaunchOn, locale)}</strong>
          </p>
        </div>
      </div>
      {/* The timezone line ("counted against your site's own timezone") is
          gone — David, 21 Sep: true, and not something anybody reading a
          countdown needs told. The server still computes against the site
          timezone (rule 9); the card just stops narrating it. */}
    </Card>
  )
}
